import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/Header';
import ContentReader from '../components/ContentReader';
import VideoModal from '../components/VideoModal';
import { BreadcrumbItem } from '../components/Header';
import { useDialog } from '../context/DialogContext';
import { progressService } from '../services/progressService';
import styles from '../styles/stepCommon.module.css';
import StepActions, { ActionItem } from '../components/StepActions';
import ReactMarkdown from 'react-markdown';
import { 
  STEP3_QUESTIONS, 
  migrateQuestionData, 
  unmappedQuestionData, 
  validateRequiredQuestions,
  type QuestionData 
} from '../utils/questionMapping';

interface Step3Props {
  user?: any;
  onLogin: () => void;
  onLogout: () => void;
}

// Using imported STEP3_QUESTIONS from questionMapping.ts

const Step3: React.FC<Step3Props> = ({ user, onLogin, onLogout }) => {
  const { showHelp, showAlert } = useDialog();
  const [isContentReaderOpen, setIsContentReaderOpen] = useState(false);
  const [isGuidanceOpen, setIsGuidanceOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [collapsedActions, setCollapsedActions] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Define the original action structure
  const originalActions: ActionItem[] = [
    {
      id: 1,
      title: "Read the SAA Green Book about Step Three",
      description: "Learn how turning life and will over to spiritual care opens the door to my transformation",
      completed: false,
      hasButton: true,
      buttonText: "Read Green Book",
      buttonAction: () => setIsContentReaderOpen(true)
    },
    {
      id: 2,
      title: "Learn how to work Step Three in SAA",
      description: "Read guidance and watch video on how to work through Step Three",
      completed: false,
      buttons: [
        {
          text: "Read Guidance",
          action: () => setIsGuidanceOpen(true),
          variant: 'primary'
        },
        {
          text: "Watch Video",
          action: () => setIsVideoModalOpen(true),
          variant: 'secondary'
        }
      ]
    },
    {
      id: 3,
      title: "Answer Step Three questions",
      description: "",
      completed: false
    },
    {
      id: 4,
      title: "Take Step Three: Pray the Third Step Prayer (my own or the AA version)",
      description: "I am uplifted by praying this prayer often",
      completed: false
    }
  ];

  const [actions, setActions] = useState<ActionItem[]>(originalActions);
  
  const [questionAnswers, setQuestionAnswers] = useState<QuestionData>({});
  const [savingQuestions, setSavingQuestions] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [savingPrayer, setSavingPrayer] = useState(false);
  const [draftAnswers, setDraftAnswers] = useState<Record<string, string>>({});
  const [editingAnswers, setEditingAnswers] = useState(false);
  
  // Auto-save state management
  const [savingAnswers, setSavingAnswers] = useState(false);
  const [savedAnswers, setSavedAnswers] = useState(false);
  const [saveAnswersError, setSaveAnswersError] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const [showValidationPopup, setShowValidationPopup] = useState(false);
  const [prayerText, setPrayerText] = useState<string>('');



  // Initialize draftAnswers when questionAnswers change
  useEffect(() => {
    if (!editingAnswers) {
      // Convert QuestionData to simple key-value format for draft
      const simpleAnswers = unmappedQuestionData(questionAnswers);
      setDraftAnswers(simpleAnswers);
    }
  }, [questionAnswers, editingAnswers]);

  // Auto-save answers with debouncing
  const autoSaveAnswers = async () => {
    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set debounce timeout (300ms)
    saveTimeoutRef.current = setTimeout(async () => {
      // Check if data actually changed
      const currentSimpleAnswers = unmappedQuestionData(questionAnswers);
      const hasChanges = JSON.stringify(draftAnswers) !== JSON.stringify(currentSimpleAnswers);
      
      if (!hasChanges) return;

      // Set saving state
      setSavingAnswers(true);
      setSaveAnswersError(false);
      setSavedAnswers(false);

      try {
        // Convert draft answers to mapped format for saving
        const mappedAnswers: QuestionData = {};
        Object.entries(draftAnswers).forEach(([key, answer]) => {
          const question = STEP3_QUESTIONS.find(q => q.key === key);
          if (question) {
            mappedAnswers[question.id] = { key, answer: answer as string };
          }
        });

        // Save to backend
        const currentActions = [...actions];
        await progressService.updateStepProgress({
          program: 'SAA',
          track: '12-steps',
          step_number: 3,
          step_title: 'Step Three',
          completed: currentActions.every(action => action.completed),
          data: {
            actions: currentActions,
            step3_questions: mappedAnswers
          }
        });

        // Update local state
        setQuestionAnswers(mappedAnswers);
        setEditingAnswers(false);

        // Show success indicator
        setSavingAnswers(false);
        setSavedAnswers(true);
        setTimeout(() => setSavedAnswers(false), 2000);

      } catch (err) {
        console.error('Error auto-saving answers:', err);
        setSavingAnswers(false);
        setSaveAnswersError(true);
        setTimeout(() => setSaveAnswersError(false), 3000);
      }
    }, 300);
  };

  // Only allow action 3 checkbox if all questions are answered
  const allQuestionsAnswered = validateRequiredQuestions(3, questionAnswers);

  // Add validation function to check if all 9 questions are answered
  const validateAction3Completion = () => {
    return STEP3_QUESTIONS.every(q => (draftAnswers[q.key] || '').trim().length > 0);
  };

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: 'SAA', path: '/saa', active: false },
    { label: '12 Steps', path: '/saa/12-steps', active: false },
    { label: 'Step Three', path: '/saa/12-steps/step-3', active: true }
  ];

  const handleGreenBookClick = () => {
    setIsContentReaderOpen(true);
  };

  // Help functions
  const openHelp = async (questionId: string) => {
    try {
      const response = await fetch('/content/SAA_Step_3_Guide.md');
      const text = await response.text();
      
      const startTag = `<<${questionId}-help start>>`;
      const endTag = `<<${questionId}-help end>>`;
      
      const startIndex = text.indexOf(startTag);
      const endIndex = text.indexOf(endTag);
      
      if (startIndex !== -1 && endIndex !== -1) {
        const helpContent = text.substring(startIndex + startTag.length, endIndex).trim();
        showHelp({
          title: 'Help',
          content: helpContent
        });
      } else {
        showHelp({
          title: 'Help',
          content: 'Help content not found for this question.'
        });
      }
    } catch (error) {
      console.error('Error loading help content:', error);
      showHelp({
        title: 'Help',
        content: 'Error loading help content.'
      });
    }
  };

  // Initialize progress tracking
  useEffect(() => {
    const initializeProgress = async () => {
      if (!user) return;
      
      setIsInitializing(true);
      
      try {
        // Initialize SAA progress
        await progressService.initializeProgress();
        
        // Fetch progress data
        const existingProgress = await progressService.getStepProgress('SAA', '12-steps', 3).catch(() => null);
        
        if (existingProgress) {
          const savedData = existingProgress.data || {};
          
          // Restore actions state
          if (savedData.actions) {
            // Merge saved actions with original actions to restore missing properties
            const mergedActions = originalActions.map(originalAction => {
              const savedAction = savedData.actions.find((a: any) => a.id === originalAction.id);
              if (savedAction) {
                return {
                  ...originalAction,
                  completed: savedAction.completed,
                  description: savedAction.description || originalAction.description
                };
              }
              return originalAction;
            });
            setActions(mergedActions);
          } else if (savedData.action_id && savedData.completed !== undefined) {
            // Handle old format: individual action completion
            const mergedActions = originalActions.map(originalAction => {
              if (originalAction.id === savedData.action_id) {
                return {
                  ...originalAction,
                  completed: savedData.completed
                };
              }
              return originalAction;
            });
            setActions(mergedActions);
          } else {
            setActions([...originalActions]);
          }
          
          // Restore question answers with migration support
          let loadedQuestionAnswers: QuestionData = {};
          
          if (savedData.step3_questions) {
            // Check if data is already in the correct format (has q1, q2, etc.)
            const firstKey = Object.keys(savedData.step3_questions)[0];
            
            if (firstKey && firstKey.startsWith('q')) {
              loadedQuestionAnswers = savedData.step3_questions;
            } else {
              loadedQuestionAnswers = migrateQuestionData(3, savedData.step3_questions);
            }
            
            setQuestionAnswers(loadedQuestionAnswers);
          } else if (savedData.questionAnswers) {
            loadedQuestionAnswers = migrateQuestionData(3, savedData.questionAnswers);
            setQuestionAnswers(loadedQuestionAnswers);
          } else if (savedData.answers) {
            // Check if data is already in new format (has q1, q2, etc.)
            const firstKey = Object.keys(savedData.answers)[0];
            if (firstKey && firstKey.startsWith('q')) {
              loadedQuestionAnswers = savedData.answers;
              setQuestionAnswers(loadedQuestionAnswers);
            } else {
              loadedQuestionAnswers = migrateQuestionData(3, savedData.answers);
              setQuestionAnswers(loadedQuestionAnswers);
            }
          }
          
          // Initialize draft answers from loaded question answers
          if (Object.keys(loadedQuestionAnswers).length > 0) {
            const simpleAnswers = unmappedQuestionData(loadedQuestionAnswers);
            setDraftAnswers(simpleAnswers);
          }
          
          // Restore prayer text
          if (savedData.prayer_text) {
            setPrayerText(savedData.prayer_text);
          }
        }
      } catch (err) {
        console.error('Error initializing progress:', err);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeProgress();
  }, [user]);

  const getCurrentActionIndex = () => {
    return actions.findIndex(action => !action.completed);
  };

  const isActionAvailable = (actionIndex: number) => {
    if (actionIndex === 0) return true;
    return actions.slice(0, actionIndex).every(a => a.completed);
  };

  const handleButtonClick = (action: ActionItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (action.buttonAction) {
      action.buttonAction();
    }
  };

  const toggleActionCollapse = (actionId: number) => {
    const newCollapsed = new Set(collapsedActions);
    if (newCollapsed.has(actionId)) {
      newCollapsed.delete(actionId);
    } else {
      newCollapsed.add(actionId);
    }
    setCollapsedActions(newCollapsed);
  };

  // Simple action change handler with dialog logic for Action 3
  const handleActionChange = async (updatedActions: ActionItem[]) => {
    if (!user) {
      setError('User not logged in. Please log in.');
      return;
    }
    
    // Check if action 3 is being marked complete
    const action3 = updatedActions.find(a => a.id === 3);
    const currentAction3 = actions.find(a => a.id === 3);
    
    let answersToSave = questionAnswers; // Default to existing saved answers
    
    if (action3 && !currentAction3?.completed && action3.completed) {
      // Check if at least one question is answered
      const atLeastOneAnswered = STEP3_QUESTIONS.some(q => (draftAnswers[q.key] || '').trim().length > 0);
      
      if (!atLeastOneAnswered) {
        // Show validation dialog
        showAlert({
          title: 'Answer Required',
          message: 'Answer at least 1 question to mark this action completed.'
        });
        return; // Don't proceed with normal completion
      }
      
      // Auto-save draft answers before marking complete
      // Convert draft answers to mapped format for saving
      const mappedAnswers: QuestionData = {};
      Object.entries(draftAnswers).forEach(([key, answer]) => {
        const question = STEP3_QUESTIONS.find(q => q.key === key);
        if (question) {
          mappedAnswers[question.id] = { key, answer: answer as string };
        }
      });
      
      // Update the answers to save
      answersToSave = mappedAnswers;
      // Update state so it's in sync
      setQuestionAnswers(mappedAnswers);
      setEditingAnswers(false);
    }
    
    // Normal action change handling for all actions
    try {
      // Update local state first
      setActions(updatedActions);
      
      // Save to backend
      const allCompleted = updatedActions.every(action => action.completed);
      await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 3,
        step_title: 'Step Three',
        completed: allCompleted,
        data: {
          actions: updatedActions,
          step3_questions: answersToSave
        }
      });
      
      // Update status note
      const completedCount = updatedActions.filter(a => a.completed).length;
      await progressService.updateStatus({
        program: 'SAA',
        track: '12-steps',
        status_note: `Working Step Three - ${completedCount} of 4 actions completed`,
        progress_milestone: allCompleted ? 'step3_completed' : 'step3_in_progress'
      });
    } catch (err) {
      console.error('Error updating action progress:', err);
      setError('Failed to save progress. Please try again.');
    }
  };

  const allCompleted = actions.every(action => action.completed);



  return (
    <div className={styles.stepPage}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogin={onLogin}
        onLogout={onLogout}
      />
      
      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <h1 className={styles.stepTitle}>Step Three</h1>
          <p className={styles.stepSubtitle}>
            Made a decision to turn our will and our lives over to the care of God as we understood Him.
          </p>
        </div>

        {isInitializing && (
          <div className={styles.loadingMessage}>
            <p>Loading your progress...</p>
          </div>
        )}

        {error && (
          <div className={styles.errorMessage}>
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Refresh Page</button>
          </div>
        )}

        {!isInitializing && !error && (
          <>
            <StepActions
          actions={actions}
          onActionChange={handleActionChange}
          renderActionContent={(action, index) => {
                        if (action.id === 3) {
              return (
                <>
                  <div className="action3ContentFullWidth" style={{ marginBottom: 16, width: '100%' }}>
                    {/* Auto-save indicator */}
                    <div style={{ 
                      textAlign: 'right', 
                      marginBottom: 16, 
                      minHeight: 24,
                      fontSize: '14px',
                      fontWeight: 500
                    }}>
                      {savingAnswers && (
                        <span style={{ color: '#6c757d' }}>
                          Saving...
                        </span>
                      )}
                      {savedAnswers && (
                        <span style={{ color: '#28a745' }}>
                          ✓ All changes saved
                        </span>
                      )}
                      {saveAnswersError && (
                        <span style={{ color: '#dc3545' }}>
                          ⚠ Save failed. Please try again.
                        </span>
                      )}
                    </div>
                    {STEP3_QUESTIONS.map((question, index) => (
                      <div key={question.key} style={{ marginBottom: 16, width: '100%' }}>
                        <div style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: 8, 
                          width: '100%'
                        }}>
                          <span style={{ 
                            fontWeight: 600, 
                            color: '#333', 
                            fontSize: '1.1rem',
                            marginRight: '8px',
                            flexShrink: 0,
                            minWidth: '20px'
                          }}>
                            {index + 1}.
                          </span>
                          <span style={{ 
                            fontWeight: 500, 
                            color: '#333', 
                            fontSize: '0.95rem',
                            lineHeight: '1.4',
                            flex: 1
                          }}>
                            {question.label}
                          </span>
                          <button 
                            type="button" 
                            className={styles.helpBtn} 
                            onClick={() => openHelp(`step3-question-${index + 1}`)}
                            title="Get help with this question"
                            style={{ marginLeft: '8px' }}
                            tabIndex={-1}
                          >
                            ?
                          </button>
                        </div>
                        <textarea
                          value={draftAnswers[question.key] || ''}
                          onChange={e => {
                            setDraftAnswers(ans => ({ ...ans, [question.key]: e.target.value }));
                            setEditingAnswers(true);
                          }}
                          onBlur={autoSaveAnswers}
                          placeholder={
                            question.key === 'decision_maker_while_acting_out' ? 'e.g., My cravings and urges made all the decisions. I didn\'t feel like I had a choice...' :
                            question.key === 'fears_about_decision' ? 'e.g., I\'m afraid nothing will change and I\'ll be disappointed...' :
                            question.key === 'problems_tried_to_resolve' ? 'e.g., I tried to quit acting out by making promises and tracking behavior...' :
                            question.key === 'self_will_suffering' ? 'e.g., I lashed out at my partner when I felt ashamed...' :
                            question.key === 'decision_maker_in_recovery' ? 'e.g., I pause and ask my Higher Power what the next right thing is...' :
                            question.key === 'concept_of_higher_power' ? 'e.g., My concept used to be punishing. Now I see my Higher Power as kind and steady...' :
                            question.key === 'examples_turned_over_to_addiction' ? 'e.g., I skipped work and lied to my partner just to act out...' :
                            question.key === 'day_if_turned_over' ? 'e.g., I\'d feel calmer and more present...' :
                            question.key === 'thoughts_behaviors_willing_to_turn_over' ? 'e.g., I\'m willing to let go of trying to fix everything myself...' :
                            'Start typing your answer here...'
                          }
                          rows={3}
                          style={{ 
                            width: '100%', 
                            borderRadius: 4, 
                            border: '1px solid #ccc', 
                            padding: '8px 12px',
                            fontSize: '14px',
                            fontFamily: 'inherit',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </>
              );
            }
            if (action.id === 4) {
              return (
                <>
                  <div style={{ marginBottom: 16, width: '100%' }}>
                    <div style={{ 
                      backgroundColor: '#f8f9fa', 
                      border: '1px solid #e0e0e0', 
                      borderRadius: 8, 
                      padding: 20, 
                      marginBottom: 20,
                      fontStyle: 'italic',
                      lineHeight: 1.6,
                      color: '#333'
                    }}>
                      <h4 style={{ 
                        margin: '0 0 12px 0', 
                        color: '#333', 
                        fontStyle: 'normal',
                        fontWeight: 600
                      }}>
                        3rd Step Prayer (from Alcoholics Anonymous, page 63)
                      </h4>
                      <p style={{ margin: '0 0 4px 0', paddingLeft: '20px' }}>
                        God, I offer myself to Thee – to build with me and to do with me as Thou wilt.
                      </p>
                      <p style={{ margin: '0 0 4px 0', paddingLeft: '20px' }}>
                        Relieve me of the bondage of self, that I may better do Thy will.
                      </p>
                      <p style={{ margin: '0 0 4px 0', paddingLeft: '20px' }}>
                        Take away my difficulties, that victory over them may bear witness to those I would help of Thy Power, Thy Love, and Thy Way of life.
                      </p>
                      <p style={{ margin: 0, paddingLeft: '20px' }}>
                        May I do Thy will always!
                      </p>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ 
                        display: 'block', 
                        fontWeight: 500, 
                        color: '#333', 
                        marginBottom: 8 
                      }}>
                        Third Step Prayer in my own words
                      </label>
                      <textarea
                        value={prayerText}
                        onChange={e => setPrayerText(e.target.value)}
                        rows={8}
                        style={{ 
                          width: '100%', 
                          borderRadius: 4, 
                          border: '1px solid #ccc', 
                          padding: 8, 
                          boxSizing: 'border-box',
                          resize: 'vertical'
                        }}
                        placeholder="Write your own version of the Third Step Prayer here..."
                      />
                    </div>
                    <div style={{ textAlign: 'right', marginTop: 16 }}>
                      <button
                        onClick={async () => {
                          setSavingPrayer(true);
                          try {
                            await progressService.updateStepProgress({
                              program: 'SAA',
                              track: '12-steps',
                              step_number: 3,
                              step_title: 'Step Three',
                              completed: actions.every(a => a.completed),
                              data: {
                                actions,
                                step3_questions: questionAnswers,
                                prayer_text: prayerText
                              }
                            });
                          } catch (err) {
                            setError('Failed to save prayer. Please try again.');
                          } finally {
                            setSavingPrayer(false);
                          }
                        }}
                        disabled={savingPrayer}
                        style={{ 
                          background: '#007bff', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: 4, 
                          padding: '8px 18px', 
                          fontWeight: 600 
                        }}
                      >
                        {savingPrayer ? 'Saving...' : 'Save Prayer'}
                      </button>
                    </div>
                  </div>
                </>
              );
            }
            return null;
          }}
        />

        {allCompleted && (
          <>
            <div className={styles.completionMessage}>
              <h3>🎉 Congratulations!</h3>
              <p>You have completed all actions for Step Three and can move on to Step Four.</p>
            </div>
            <div className={styles.stepNavigation}>
              <div className={styles.navigationLinks}>
                <a href="/saa/12-steps/step-2" className={styles.prevStepLink}>
                  {'<<'} Step Two
                </a>
                <span></span>
                <a href="/saa/12-steps/step-4" className={styles.nextStepLink}>
                  Step Four {'>>'}
                </a>
              </div>
            </div>
          </>
        )}

        {!allCompleted && (
          <div className={styles.stepNavigation}>
            <div className={styles.navigationLinks}>
              <a href="/saa/12-steps/step-2" className={styles.prevStepLink}>
                {'<<'} Step Two
              </a>
              <span></span>
              <a href="/saa/12-steps/step-4" className={styles.nextStepLink}>
                Step Four {'>>'}
              </a>
            </div>
          </div>
        )}
          </>
        )}
      </main>

      {/* ContentReader for Green Book */}
      <ContentReader
        isOpen={isContentReaderOpen}
        onClose={() => setIsContentReaderOpen(false)}
        filePath="/content/010_CHAPTER_THREE.md"
        startTag="<<step-three start>>"
        endTag="<<step-three end>>"
      />

      {/* ContentReader for How to Work Step Three */}
      <ContentReader
        isOpen={isGuidanceOpen}
        onClose={() => setIsGuidanceOpen(false)}
        filePath="/content/SAA_Step_3_Guide.md"
        startTag="<<step3-guidance start>>"
        endTag="<<step3-guidance end>>"
        title="Guidance on working Step Three"
      />



      {/* Validation Popup Modal */}
      {showValidationPopup && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%', 
          backgroundColor: 'rgba(0,0,0,0.5)', 
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{ 
            backgroundColor: 'white', 
            padding: 24, 
            borderRadius: 8, 
            maxWidth: 400, 
            width: '90%',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#333' }}>Action Not Complete</h3>
            <p style={{ margin: '0 0 24px 0', color: '#666', lineHeight: 1.4 }}>
              Enter answers for questions 1-9 to mark this action complete.
            </p>
            <button
              onClick={() => setShowValidationPopup(false)}
              style={{ 
                background: '#007bff', 
                color: 'white', 
                border: 'none', 
                borderRadius: 4, 
                padding: '8px 24px', 
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Ok
            </button>
          </div>
        </div>
      )}
      
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoId="Tq16X-V_fFQ"
        title="Step Three Video Guide"
      />
    </div>
  );
};

export default Step3; 